import { addDays, format, isSameDay, parse, subYears } from "date-fns";
import { DEFAULT_NEXT_ACTIVATION_DATE } from "@constants";

export const FULL_DATE_FORMAT = "dd/MM/yyyy";

export const DATE_EXPIRY_FORMAT = "MM/yyyy";

export const INSTALLATION_DATE_FORMAT = "dd MMMM yyyy";

export const formatDateExpiry = (date: Date) => format(date, DATE_EXPIRY_FORMAT);

export const formatInstallationDate = (date: Date) => format(date, INSTALLATION_DATE_FORMAT);

export const getDefaultActivationDate = (): Date => addDays(new Date(), DEFAULT_NEXT_ACTIVATION_DATE);

export const deduplicateDatesByCalendarDay = (dates: readonly Date[]): Date[] => {
    const result: Date[] = [];

    for (const date of dates) {
        if (!result.some((existing) => isSameDay(existing, date))) {
            result.push(date);
        }
    }

    return result;
};

/** Matches a strict `dd/MM/yyyy` string before parsing */
export const BIRTH_DATE_DISPLAY_STRING_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

export const MIN_BIRTH_DATE_YEAR = 1900;

/** Earliest selectable birth date in date pickers. */
export const MIN_SELECTABLE_BIRTH_DATE = new Date(MIN_BIRTH_DATE_YEAR, 0, 1);

export const LEGAL_ADULT_MIN_AGE_YEARS = 18;

/**
 * Latest birth date still allowed in a picker so the person is at least {@link LEGAL_ADULT_MIN_AGE_YEARS} years old.
 */
export const MAX_SELECTABLE_BIRTHDATE_FOR_ADULT: Date = subYears(new Date(), LEGAL_ADULT_MIN_AGE_YEARS);

/**
 * Parses a birth date string shown as {@link FULL_DATE_FORMAT}, or null if empty / invalid shape.
 */
export const parseBirthDateDisplayString = (birthDateString: string | undefined | null): Date | null => {
    if (!birthDateString || !BIRTH_DATE_DISPLAY_STRING_REGEX.test(birthDateString)) {
        return null;
    }

    return parse(birthDateString, FULL_DATE_FORMAT, new Date());
};

/**
 * Builds a DD/MM/YYYY display string from up to 8 digits, inserting `/` after the day and month segments.
 */
export const formatDigitsToBirthDateDisplay = (raw: string): string => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length === 0) {
        return "";
    }
    const day = digits.slice(0, 2);
    if (digits.length <= 2) {
        return digits.length === 2 ? `${day}/` : day;
    }
    const month = digits.slice(2, 4);
    if (digits.length <= 4) {
        return digits.length === 4 ? `${day}/${month}/` : `${day}/${digits.slice(2)}`;
    }

    return `${day}/${month}/${digits.slice(4)}`;
};

/**
 * If the user deleted a slash without changing the digit count,
 * removes the previous digit so backspace behaves naturally on masked values.
 */
export const normalizeBirthDateInput = (previousFormatted: string, nextRaw: string): string => {
    const previousDigits = previousFormatted.replace(/\D/g, "");
    const nextDigits = nextRaw.replace(/\D/g, "").slice(0, 8);

    if (nextDigits === previousDigits && nextRaw.length < previousFormatted.length) {
        return formatDigitsToBirthDateDisplay(previousDigits.slice(0, -1));
    }

    return formatDigitsToBirthDateDisplay(nextRaw);
};

export const getNormalizedBirthDateChange = (
    previousFormatted: string,
    nextRaw: string,
    selectionStart: number | null
): { value: string; caret: number } => {
    const value = normalizeBirthDateInput(previousFormatted, nextRaw);

    const selectionIndex = selectionStart ?? nextRaw.length;
    const rawEnd = Math.min(Math.max(selectionIndex, 0), nextRaw.length);
    let digitsBeforeCursor = 0;
    for (let i = 0; i < rawEnd; i += 1) {
        const characterPosition = nextRaw[i];
        if (characterPosition !== undefined && /\d/.test(characterPosition)) {
            digitsBeforeCursor += 1;
        }
    }

    const totalDigits = value.replace(/\D/g, "").length;
    const cappedTarget = Math.min(digitsBeforeCursor, totalDigits);

    let caret = 0;
    if (cappedTarget > 0) {
        let matchedDigits = 0;
        let placed = false;
        for (let i = 0; i < value.length; i += 1) {
            const characterPosition = value[i];
            if (characterPosition === undefined || !/\d/.test(characterPosition)) {
                continue;
            }
            matchedDigits += 1;
            if (matchedDigits === cappedTarget) {
                const afterDigit = i + 1;
                caret = value[afterDigit] === "/" ? afterDigit + 1 : afterDigit;
                placed = true;
                break;
            }
        }
        if (!placed) {
            caret = value.length;
        }
    }

    caret = Math.min(Math.max(caret, 0), value.length);

    return { value, caret };
};
